<template>
  <div class="form-section">
    <h3 class="text-responsive-base font-bold text-center mb-responsive text-gray-800">
      {{ title || 'Review and Submit' }}
    </h3>
    
    <div class="p-4 border rounded-md bg-white shadow-sm mb-6">
      <div class="space-y-responsive">
        <!-- Effective Date Field -->
        <ResponsiveDateInput
          label="Effective Date"
          field-id="effective-date"
          :model-value="effectiveDate"
          @update:model-value="$emit('update:effectiveDate', $event)"
          :min-date="minDate"
          :error="errors.effectiveDate"
          is-primary
          required
          help-text="This is the date when your requested changes will take effect."
          description="Please select the earliest date you want these changes to take effect."
          :userRole="customerType"
        />
        
        <!-- Additional Fields Slot -->
        <slot name="additional-fields"></slot>
        
        <!-- Comments Field -->
        <FormField
          label="Additional Comments"
          field-id="comments"
          :error="errors.comments"
          :show-optional-label="true"
          help-text="Feel free to add any relevant details that might help us process your request."
        >
          <textarea
            id="comments"
            :value="comments"
            @input="$emit('update:comments', $event.target.value)"
            rows="4"
            class="input-responsive resize-none"
            placeholder="Add any relevant details or special instructions..."
          ></textarea>
        </FormField>
      </div>
    </div>
      
    <!-- Terms and Conditions -->
    <div class="form-field bg-gray-50 p-4 rounded-md border border-gray-200 mb-6">
      <p class="text-sm text-gray-600 mb-2">
        By submitting this form, you acknowledge that:
      </p>
      <ul class="list-disc pl-5 text-sm text-gray-600 space-y-1">
        <li>The information provided is accurate to the best of your knowledge</li>
        <li>An agent may contact you to verify details or request additional information</li>
        <li>Changes to your policy may result in premium adjustments</li>
      </ul>
    </div>
      
    <!-- Error Message Display -->
    <div v-if="errors.submission" class="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <Icon icon="mdi:alert-circle" class="h-5 w-5 text-red-500" />
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-700">
            {{ errors.submission }}
          </p>
        </div>
      </div>
    </div>
    
    <!-- Action Buttons -->
    <div class="flex-responsive-col justify-between mt-responsive">
      <button
        @click="$emit('previous')"
        class="btn-responsive-secondary mb-2 sm:mb-0 mobile-enhanced-button"
        :disabled="isSubmitting"
      >
        Back
      </button>
      <button
        @click="$emit('submit')"
        :class="customerType === 'lender' ? 'btn-lender' : 'btn-customer'"
        :disabled="isSubmitting || !props.isStepCurrentlyValid"
        class="mobile-enhanced-button"
      >
        <span v-if="isSubmitting">
          <Icon icon="eos-icons:loading" class="animate-spin mr-2" />
          Submitting...
        </span>
        <span v-else>Submit Request</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import FormField from '@/components/common/FormField.vue';
import ResponsiveDateInput from '@/components/common/ResponsiveDateInput.vue';
import { getTodayFormatted } from '@/utils/dateUtils';

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  effectiveDate: {
    type: String,
    required: true
  },
  comments: {
    type: String,
    required: true
  },
  customerType: {
    type: String,
    default: 'customer'
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  isSubmitting: {
    type: Boolean,
    default: false
  },
  isStepCurrentlyValid: {
    type: Boolean,
    default: true
  }
});

// Calculate minimum date (today)
const minDate = computed(() => getTodayFormatted());

// Define emits
defineEmits([
  'update:effectiveDate',
  'update:comments',
  'previous',
  'submit'
]);
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.form-field {
  @apply mb-6;
}

.flex-responsive-col {
  @apply flex flex-col sm:flex-row sm:justify-between;
}

.btn-responsive-secondary {
  @apply px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors;
}

.input-responsive {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm;
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
  @apply focus:outline-none;
}

.space-y-responsive {
  @apply space-y-4 md:space-y-6;
}

.mb-responsive {
  @apply mb-4 md:mb-6;
}

.mt-responsive {
  @apply mt-4 md:mt-6;
}

.text-responsive-base {
  @apply text-lg md:text-xl;
}

.mobile-enhanced-button {
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
}
</style> 